import { motion } from "framer-motion";
import { Flag, Gift, Megaphone, Rocket, Trophy, Zap } from "lucide-react";
import MilestoneChart from "@/components/MilestoneChart";

const EASE = [0.22, 1, 0.36, 1];

export const STOPS = [
  {
    mc: "20K MC",
    value: 20000,
    title: "First Rally",
    rewards: [{ icon: Rocket, text: "10x Dev Boost" }],
  },
  {
    mc: "35K MC",
    value: 35000,
    title: "Getting Official",
    rewards: [{ icon: Flag, text: "Dexscreener Paid" }],
  },
  {
    mc: "GRADUATED",
    value: 50000,
    title: "Off the Bench",
    rewards: [{ icon: Rocket, text: "10x Dev Boost" }],
  },
  {
    mc: "100K MC",
    value: 100000,
    title: "Six-Figure Territory",
    rewards: [{ icon: Gift, text: "$500 Giveaway — 5 winners take $100 each" }],
  },
  {
    mc: "500K MC",
    value: 500000,
    title: "Half a Million Strong",
    rewards: [
      { icon: Gift, text: "$500 Giveaway — 5 winners take $100 each" },
      { icon: Rocket, text: "10x Dev Boost" },
    ],
  },
  {
    mc: "1M MC +",
    value: 1000000,
    title: "The Endgame",
    rewards: [
      { icon: Trophy, text: "Active giveaways every single day" },
      { icon: Zap, text: "Non-stop boosts" },
    ],
    finale: true,
  },
];

function Stop({ stop, index }) {
  const left = index % 2 === 0;
  return (
    <div className={`relative flex ${left ? "lg:justify-start" : "lg:justify-end"}`}>
      {/* node on the line */}
      <div
        className={`absolute left-5 top-8 z-10 flex h-5 w-5 -translate-x-1/2 items-center justify-center lg:left-1/2 ${
          stop.finale ? "" : ""
        }`}
      >
        <span
          className={`h-3.5 w-3.5 rounded-full border-2 ${
            stop.finale
              ? "border-[#F6C85F] bg-[#E5A93C] shadow-[0_0_20px_rgba(246,200,95,0.8)]"
              : "border-[#E5A93C] bg-[#07080A]"
          }`}
        />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-60px" }}
        transition={{ duration: 0.75, ease: EASE }}
        whileHover={{ y: -4 }}
        className={`ml-14 w-full lg:ml-0 lg:w-[calc(50%-3rem)] ${
          stop.finale
            ? "rounded-2xl border border-[rgba(246,200,95,0.6)] bg-gradient-to-br from-[#141720] to-[#0F1E36] p-7 shadow-[0_0_50px_rgba(229,169,60,0.12)] sm:p-8"
            : "rounded-2xl border border-[rgba(229,169,60,0.22)] bg-[#0E1015] p-7 transition-colors duration-300 hover:border-[rgba(246,200,95,0.5)] sm:p-8"
        }`}
        data-testid={`roadmap-stop-${index + 1}`}
      >
        <div className="flex items-center justify-between gap-3">
          <span
            className={`rounded-full px-4 py-1.5 font-mono2 text-xs font-bold tracking-wider ${
              stop.finale
                ? "bg-[#E5A93C] text-[#07080A]"
                : "border border-[rgba(229,169,60,0.4)] text-[#F6C85F]"
            }`}
          >
            {stop.mc}
          </span>
          <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#64748B]">
            Stop 0{index + 1}
          </span>
        </div>
        <h3
          className={`mt-4 font-display text-xl font-extrabold uppercase tracking-tight sm:text-2xl ${
            stop.finale ? "text-[#F6C85F]" : "text-[#F8FAFC]"
          }`}
        >
          {stop.title}
        </h3>
        <ul className="mt-4 space-y-2.5">
          {stop.rewards.map((r) => (
            <li key={r.text} className="flex items-center gap-2.5 text-sm text-[#94A3B8] sm:text-base">
              <r.icon className="h-4 w-4 shrink-0 text-[#E5A93C]" />
              {r.text}
            </li>
          ))}
        </ul>
      </motion.div>
    </div>
  );
}

export default function Roadmap() {
  return (
    <section id="roadmap" className="relative overflow-hidden py-28 sm:py-36" data-testid="roadmap-section">
      <div className="pointer-events-none absolute -left-32 top-1/3 h-[420px] w-[420px] rounded-full bg-[#0F1E36] opacity-50 blur-[130px]" />
      <div className="relative mx-auto max-w-6xl px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, ease: EASE }}
          className="text-center"
        >
          <p className="flex items-center justify-center gap-2.5 font-mono2 text-xs uppercase tracking-[0.3em] text-[#E5A93C]">
            <Megaphone className="h-4 w-4" />
            07 / The Campaign Trail
          </p>
          <h2 className="mx-auto mt-5 max-w-2xl font-display text-2xl font-extrabold uppercase leading-tight tracking-tight sm:text-3xl lg:text-4xl">
            Every milestone <span className="text-stroke-gold">pays the community.</span>
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-[#94A3B8] sm:text-base">
            Market cap targets with real rewards attached. The higher we climb, the louder the
            boosts and the bigger the giveaways.
          </p>
        </motion.div>

        <MilestoneChart />

        <div className="relative mt-16 space-y-10 lg:space-y-14">
          {/* trail line */}
          <div className="absolute bottom-6 left-5 top-6 w-px -translate-x-1/2 bg-gradient-to-b from-[rgba(229,169,60,0.5)] via-[rgba(229,169,60,0.25)] to-[rgba(246,200,95,0.6)] lg:left-1/2" />
          {STOPS.map((stop, i) => (
            <Stop key={stop.mc} stop={stop} index={i} />
          ))}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="mt-14 text-center font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#64748B]"
        >
          One ticker. One movement. <span className="text-[#F6C85F]">Unlimited meme potential.</span>
        </motion.p>
      </div>
    </section>
  );
}
